'use client';

import type { EmblaOptionsType } from 'embla-carousel';
import useEmblaCarousel from 'embla-carousel-react';
import Image from 'next/image';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';
import { imgLoader } from '~/shared/lib';
import type { BannerSlideGetPayload } from '../../../../../../packages/db/dist/src/generated/models/BannerSlide';

type BannerSlideWithImages = BannerSlideGetPayload<{
    include: { desktopImage: true; mobileImage: true };
}>;

type BannerSliderProps = {
    slides: BannerSlideWithImages[];
    deviceType: 'console' | 'mobile' | 'tablet' | 'smarttv' | 'wearable' | 'embedded' | 'unknown';
};

const emblaOptions: EmblaOptionsType = {
    loop: true,
    skipSnaps: false,
    align: 'start',
};

export const BannerSlider = ({ slides, deviceType }: BannerSliderProps) => {
    const [emblaRef, emblaApi] = useEmblaCarousel(emblaOptions);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [lastInteractionTime, setLastInteractionTime] = useState<number>(Date.now());

    const scrollPrev = useCallback(() => {
        if (!emblaApi) return;
        setLastInteractionTime(Date.now());
        emblaApi.scrollPrev();
    }, [emblaApi]);

    const scrollNext = useCallback(() => {
        if (!emblaApi) return;
        setLastInteractionTime(Date.now());
        emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback(() => {
        if (!emblaApi) return;
        setSelectedIndex(emblaApi.selectedScrollSnap());
    }, [emblaApi]);

    useEffect(() => {
        if (!emblaApi) return;

        emblaApi.on('select', onSelect);
        onSelect();
    }, [emblaApi, onSelect]);

    useEffect(() => {
        if (!emblaApi) return;

        const interval = setInterval(() => {
            const now = Date.now();
            const timeSinceLastInteraction = now - lastInteractionTime;

            if (timeSinceLastInteraction > 5000) {
                emblaApi.scrollNext();
            }
        }, 6000);

        return () => clearInterval(interval);
    }, [emblaApi, lastInteractionTime]);

    if (!slides || slides.length === 0) return null;

    return (
        <div className='relative w-full overflow-hidden'>
            <div ref={emblaRef} className='overflow-hidden'>
                <div className='flex'>
                    {slides.map(slide => {
                        const image = deviceType === 'mobile' ? slide?.mobileImage : slide?.desktopImage;
                        if (!image?.key) return null;

                        return (
                            <Link
                                key={slide.id}
                                href={slide.link || '#'}
                                className='relative min-h-page w-full flex-shrink-0'
                            >
                                <Image
                                    loader={imgLoader({ src: image.key, width: image.width, quality: 75 })}
                                    src={image.key}
                                    placeholder='blur'
                                    blurDataURL={image.lqip ?? undefined}
                                    alt={image.alt || ''}
                                    fill
                                    className='object-cover'
                                    sizes='(max-width: 768px) 768px, (max-width: 1024px) 1024px, (max-width: 1536px) 1536px, 2048px'
                                />
                            </Link>
                        );
                    })}
                </div>
            </div>

            <button
                type='button'
                onClick={scrollPrev}
                className='absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-neutral-900/50 p-2 text-white hover:bg-neutral-900 focus:outline-none'
                aria-label='Previous Slide'
            >
                ‹
            </button>
            <button
                type='button'
                onClick={scrollNext}
                className='absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full bg-neutral-900/50 p-2 text-white hover:bg-neutral-900 focus:outline-none'
                aria-label='Next Slide'
            >
                ›
            </button>

            <div className='absolute bottom-4 left-1/2 flex -translate-x-1/2 gap-2'>
                {slides.map((slide, idx) => (
                    <button
                        type='button'
                        key={slide.id}
                        onClick={() => {
                            setLastInteractionTime(Date.now());
                            emblaApi?.scrollTo(idx);
                        }}
                        className={`h-2 w-2 rounded-full ${selectedIndex === idx ? 'bg-neutral-900' : 'bg-neutral-400'}`}
                    />
                ))}
            </div>
        </div>
    );
};
